// [PURPOSE]: Property-detection helper for the `surfacesCombatToggle`
//   data-block field. Walks a player's owned data (known/prepared
//   spells, active subclass-option picks, resolved class+subclass
//   features at current level) and returns true if any of them
//   declares the given combat-toggle flag.
//
// [WHY]: Phase B / #035. Pre-fix combatToggles.js's `isRelevant`
//   predicates for layered / state-gated / multi-subclass toggles
//   had hand-rolled string matches:
//
//     huntersMarkTargetActive: s.name === "Hunter's Mark"
//     targetBelowFullHp: subclass === 'Hunter' && subclassData.hunterOptions.3 === 'Colossus Slayer'
//     castingClericCantripDamage: className === 'Cleric' && level >= 8 &&
//       ['Knowledge Domain', 'Light Domain', 'Grave Domain', 'Peace Domain'].includes(subclass)
//
//   All three are string-domain-key anti-patterns (Rule 11 / no
//   string-domain matching). The new schema moves the "this thing
//   surfaces this combat toggle" relationship into a single field on
//   the data block that owns the mechanic:
//
//     // Hunter's Mark spell (in srdSpells.js)
//     { name: "Hunter's Mark", ..., surfacesCombatToggle: 'huntersMarkTargetActive' }
//
//     // Colossus Slayer subclass-option choice (in srdSubclassFeatures.js)
//     'Colossus Slayer': { effects: [...], surfacesCombatToggle: 'targetBelowFullHp' }
//
//     // Potent Spellcasting feature on each Domain subclass (in srdSubclassFeatures.js)
//     'Potent Spellcasting': { minLevel: 8, effects: [...],
//       surfacesCombatToggle: 'castingClericCantripDamage' }
//
//   The helper scans appropriate sources and returns true on the
//   first match. No string-domain-key match. No subclass-name
//   OR-list. No id derivation.
//
// [INTEGRATION]: combatToggles.js's `isRelevant` predicates for the
//   3 affected toggles call `surfacesCombatToggleFlag(playerStats, flag)`.
//   Custom CW spells / subclass options / features that declare the
//   field are picked up automatically.

import { getActiveSubclassFeatures as defaultGetActiveSubclassFeatures } from '../data/srdSubclassFeatures';
import { SRD_SPELLS } from '../data/srdSpells';

/**
 * Default spell-name lookup using SRD_SPELLS. Most callers don't
 * override this; it's an injectable seam for unit tests so the
 * helper isn't forced to import the SRD dataset in test runs.
 */
function defaultLookupSpellByName(name) {
  if (typeof name !== 'string') return null;
  for (const levelSpells of Object.values(SRD_SPELLS || {})) {
    if (!Array.isArray(levelSpells)) continue;
    const found = levelSpells.find((s) => s?.name === name);
    if (found) return found;
  }
  return null;
}

/**
 * Default subclass-choice lookup using SRD_SUBCLASS_FEATURES (via the
 * getActiveSubclassFeatures helper).
 */
function defaultLookupSubclassChoice(className, subclassName, choiceFeatureKey, choiceName, getFeatures) {
  if (!className || !subclassName || !choiceFeatureKey || !choiceName) return null;
  const features = getFeatures(className, subclassName, 99);  // 99 = "any level"
  for (const [key, feature] of features) {
    if (key !== choiceFeatureKey) continue;
    return feature?.choices?.[choiceName] || null;
  }
  return null;
}

/**
 * Read a value at a dotted path. Used to resolve `subclassData.<choiceKey>`
 * picks where choiceKey itself contains a dot (e.g., 'hunterOptions.3').
 */
function readPath(obj, path) {
  if (!obj || typeof path !== 'string') return undefined;
  const parts = path.split('.');
  let cur = obj;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[part];
  }
  return cur;
}

/**
 * Does any of the player's owned data declare ownership of the given
 * combat-toggle flag via the `surfacesCombatToggle` field?
 *
 * Sources scanned (short-circuits on first match):
 *   1. Known/prepared spells (`playerStats.knownSpells`,
 *      `playerStats.preparedSpells`). Spells stored as objects are
 *      checked directly; spells stored as bare names are resolved
 *      via the injected `lookupSpellByName` (default: SRD_SPELLS scan).
 *   2. Resolved class+subclass features at current level via the
 *      injected `getActiveSubclassFeatures` (default: SRD_SUBCLASS_FEATURES).
 *   3. Active subclass-option picks at `subclassData.<choiceKey>`,
 *      with the choice resolved via the injected `lookupSubclassChoice`.
 *
 * @param {object|null} playerStats
 * @param {string} flag — toggle flag name (e.g., 'huntersMarkTargetActive')
 * @param {object} [opts]
 * @param {(name: string) => object|null} [opts.lookupSpellByName]
 * @param {(className: string, subclassName: string, level: number) => Array<[string, object]>} [opts.getActiveSubclassFeatures]
 * @param {(className: string, subclassName: string, choiceFeatureKey: string, choiceName: string) => object|null} [opts.lookupSubclassChoice]
 * @returns {boolean}
 */
export function surfacesCombatToggleFlag(playerStats, flag, opts = {}) {
  if (!playerStats || !flag) return false;

  const lookupSpellByName = opts.lookupSpellByName || defaultLookupSpellByName;
  const getActiveSubclassFeatures = opts.getActiveSubclassFeatures || defaultGetActiveSubclassFeatures;
  const lookupSubclassChoice = opts.lookupSubclassChoice
    || ((cn, sc, ck, ch) => defaultLookupSubclassChoice(cn, sc, ck, ch, getActiveSubclassFeatures));

  // [SOURCE 1]: known/prepared spells.
  const allSpells = [
    ...(Array.isArray(playerStats.knownSpells) ? playerStats.knownSpells : []),
    ...(Array.isArray(playerStats.preparedSpells) ? playerStats.preparedSpells : []),
  ];
  for (const s of allSpells) {
    if (!s) continue;
    let spellData = s;
    if (typeof s === 'string') {
      spellData = lookupSpellByName(s);
    }
    if (spellData?.surfacesCombatToggle === flag) return true;
  }

  // [SOURCE 2 + 3]: per-class scan of subclass features + option picks.
  const classes = Array.isArray(playerStats.classes) ? playerStats.classes : [];
  for (const c of classes) {
    if (!c || !c.className) continue;
    const classLevel = c.level || 0;

    if (!c.subclass) continue;
    const features = getActiveSubclassFeatures(c.className, c.subclass, classLevel) || [];

    for (const [featureKey, feature] of features) {
      // [SOURCE 2]: feature itself declares the toggle.
      if (feature?.surfacesCombatToggle === flag) return true;

      // [SOURCE 3]: feature is a choice block with a player pick;
      // resolve the pick via subclassData and check its declaration.
      if (feature?.choiceKey && c.subclassData) {
        const pickedName = readPath(c.subclassData, feature.choiceKey);
        if (typeof pickedName === 'string') {
          const choiceData = lookupSubclassChoice(
            c.className,
            c.subclass,
            featureKey,
            pickedName
          );
          if (choiceData?.surfacesCombatToggle === flag) return true;
        }
      }
    }
  }

  return false;
}
